import {refreshFetch} from './refreshFetch';
import {getAndHandleResponseError, getURL} from '../../utils';
import Client from '../../Client';

const RETRY_RESPONSE_STATUSES = [500, 502, 503, 504, 400];
const MAX_RETRIES_NUMBER = 1;

type ResponseError = {
  response: {status: 500 | 503 | 504};
  body: ResponseError;
};

type ResolverSettings = {
  customURL?: string;
  retries?: number;
  errorRetryCount?: number;
  version?: string | number;
  requestWithOutToken?: boolean;
};

type Options = {
  signal?: {
    aborted: boolean;
  };
};

function defaultRetryDelay(failureCount: number) {
  return Math.min(1000 * 2 ** failureCount, 30000);
}
export function sleep(timeout: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
}

async function resolver<T = any>(
  endpoint: string,
  options?: (Options & RequestInit) | null,
  settings?: ResolverSettings,
): Promise<T> {
  try {
    const response = await refreshFetch(
      settings?.customURL || getURL(endpoint, settings?.version),
      options || {},
    );

    return response?.body as T;
  } catch (error: any) {
    const retries = settings?.retries ?? 0;
    const globalMaxRequestsRetriesCount = Client.initParams?.maxRequestsRetriesCount;
    const maxRetryCount =
      settings?.errorRetryCount ?? globalMaxRequestsRetriesCount ?? MAX_RETRIES_NUMBER;
    if (
      RETRY_RESPONSE_STATUSES.includes(error?.response?.status) &&
      retries < maxRetryCount
    ) {
      await sleep(defaultRetryDelay(retries));
      return resolver(endpoint, options, {
        ...settings,
        retries: retries + 1,
      });
    }

    const responseError = getAndHandleResponseError(
      error.response,
      error.body,
      error?.message,
    );

    if (responseError) {
      throw responseError;
    }

    throw error.body;
  }
}

export default resolver;
export type {ResolverSettings};
